import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import logoLightDolfin from "../logo-light.svg";
import logoDarkDolfin from "../logo-dark.svg";

const COOKIE_NAME = "dolfin-brand";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

const DOLFIN_DEFAULTS: BrandConfig = {
  name: "Dolfin",
  logoLight: logoLightDolfin,
  logoDark: logoDarkDolfin,
  primary: "#000c42",
};

export interface BrandConfig {
  name: string;
  logoLight: string;
  logoDark: string;
  primary: string;
}

export interface PartialBrandConfig {
  name?: string | null;
  logoLight?: string | null;
  logoDark?: string | null;
  primary?: string | null;
}

interface BrandContextValue extends BrandConfig {
  isCustom: boolean;
  setBrand: (next: PartialBrandConfig) => void;
  clearBrand: () => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

function readCookie(): PartialBrandConfig | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.slice(COOKIE_NAME.length + 1)));
  } catch {
    return null;
  }
}

function writeCookie(value: PartialBrandConfig) {
  if (typeof document === "undefined") return;
  const encoded = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

function deleteCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
}

function resolveBrand(partial: PartialBrandConfig | null): BrandConfig {
  if (!partial) return DOLFIN_DEFAULTS;
  const light = partial.logoLight || partial.logoDark || DOLFIN_DEFAULTS.logoLight;
  const dark = partial.logoDark || partial.logoLight || DOLFIN_DEFAULTS.logoDark;
  return {
    name: partial.name || DOLFIN_DEFAULTS.name,
    logoLight: light,
    logoDark: dark,
    primary: partial.primary || DOLFIN_DEFAULTS.primary,
  };
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function readableForeground(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#ffffff";
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLin(rgb.r) + 0.7152 * toLin(rgb.g) + 0.0722 * toLin(rgb.b);
  return L > 0.45 ? "#0b1020" : "#ffffff";
}

function applyPrimaryToDocument(primary: string) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const fg = readableForeground(primary);
  root.style.setProperty("--primary", primary);
  root.style.setProperty("--primary-foreground", fg);
  root.style.setProperty("--sidebar-primary", primary);
  root.style.setProperty("--sidebar-primary-foreground", fg);
  root.style.setProperty("--brand", primary);
  root.style.setProperty("--brand-foreground", fg);
}

function clearPrimaryFromDocument() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.removeProperty("--primary");
  root.style.removeProperty("--primary-foreground");
  root.style.removeProperty("--sidebar-primary");
  root.style.removeProperty("--sidebar-primary-foreground");
  root.style.removeProperty("--brand");
  root.style.removeProperty("--brand-foreground");
}

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [partial, setPartial] = useState<PartialBrandConfig | null>(() => readCookie());

  const resolved = useMemo(() => resolveBrand(partial), [partial]);
  const isCustom = partial !== null;

  useEffect(() => {
    if (isCustom) {
      applyPrimaryToDocument(resolved.primary);
    } else {
      clearPrimaryFromDocument();
    }
  }, [isCustom, resolved.primary]);

  const setBrand = useCallback((next: PartialBrandConfig) => {
    writeCookie(next);
    setPartial(next);
  }, []);

  const clearBrand = useCallback(() => {
    deleteCookie();
    setPartial(null);
  }, []);

  const value = useMemo<BrandContextValue>(
    () => ({ ...resolved, isCustom, setBrand, clearBrand }),
    [resolved, isCustom, setBrand, clearBrand],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrand must be used inside BrandProvider");
  return ctx;
}

export const BRAND_DEFAULTS = DOLFIN_DEFAULTS;
